/**
 * Search-history adapter — the single caller of `/api/search-history*`.
 *
 * Keeps `fetch('/api/...')` out of `hooks/use-search-history.ts` (HARD GATE —
 * CLAUDE.md / SEARCH_AUDIT §14.3). Behaviour is preserved exactly: `load` and
 * `syncLogin` throw on non-OK (callers depend on it); the fire-and-forget
 * mutations (logout sync, add, delete, clear) don't check status — the hook
 * already falls back to a reload on throw.
 */
export type SearchHistoryRow = {
  term: string | null;
  term_normalized: string | null;
  updated_at: string | null;
};

type Identity = { userId: string | null; guestId: string };

export async function fetchSearchHistory(identity: Identity): Promise<SearchHistoryRow[]> {
  const params = new URLSearchParams();
  if (identity.userId) {
    params.set("userId", identity.userId);
  } else {
    params.set("guestId", identity.guestId);
  }
  const res = await fetch(`/api/search-history?${params.toString()}`, {
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  });
  if (!res.ok) {
    throw new Error(`Search history load failed: ${res.status}`);
  }
  return (await res.json()) as SearchHistoryRow[];
}

export async function syncSearchHistoryLogin(opts: { userId: string; guestId: string }): Promise<void> {
  const response = await fetch("/api/search-history/sync-login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId: opts.userId, guestId: opts.guestId }),
  });
  if (!response.ok) {
    throw new Error(`Sync login failed: ${response.status}`);
  }
}

export async function syncSearchHistoryLogout(opts: { userId: string; guestId: string }): Promise<void> {
  await fetch("/api/search-history/sync-logout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId: opts.userId, guestId: opts.guestId }),
  });
}

export async function postSearchHistoryTerm(opts: {
  term: string;
  userId: string | null;
  guestId: string;
  /**
   * B2 (demand-funnel instrumentation) — number of listings this term's
   * search returned. Optional: the "add to history" call (commitSearch)
   * fires BEFORE results are known, so it omits this field; a second call
   * once results settle (hooks/use-search-history.ts recordResultCount)
   * supplies it. The route upserts by (term_normalized, identity), so the
   * second call updates the SAME row rather than creating a duplicate.
   */
  resultCount?: number;
}): Promise<void> {
  await fetch("/api/search-history", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      term: opts.term,
      userId: opts.userId,
      guestId: opts.guestId,
      ...(opts.resultCount !== undefined ? { resultCount: opts.resultCount } : {}),
    }),
  });
}

export async function deleteSearchHistoryTerm(opts: {
  term: string;
  userId: string | null;
  guestId: string;
}): Promise<void> {
  await fetch("/api/search-history", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ term: opts.term, userId: opts.userId, guestId: opts.guestId }),
  });
}

export async function clearSearchHistory(opts: { userId: string | null; guestId: string }): Promise<void> {
  await fetch("/api/search-history", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ all: true, userId: opts.userId, guestId: opts.guestId }),
  });
}
